import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    formatDollars,
    DefaultIntlFormatValues,
} from '@ratehub/base-ui';

import COMMON_MESSAGES from '../definitions/Messages';
import CreditCardShape from '../definitions/CreditCardShape';
import CardCalculationModal from './CardCalculationModal';
import CardCalculationHeader from './CardCalculationHeader';


function CardCalculationModalRewards({ cardInfo, isOpen = true, onClose, ...otherProps }) {
    const intl = useIntl();

    const {
        netFirstYearValue,
        netSubsequentYearValue,
        subsequentYearPointsInDollars,
        isFirstYearFeeWaived,
        monthlySpendingAverage,
        firstYearRewardPoints,
        firstYearPointsInDollars,
        welcomeSignUp,
        welcomeSpending,
        giftCardOffer,
        annualFee,
        anniversaryBonus,
        name,
        cardImageURL,
    } = cardInfo;


    return (
        <CardCalculationModal
            isOpen={isOpen}
            onClose={onClose}
            {...otherProps}
        >
            <>
                <CardCalculationModal.DefinitionListWrapper>
                    <CardCalculationHeader
                        cardName={name}
                        cardImageURL={cardImageURL}
                    />

                    <CardCalculationModal.ContentGrid className="second-column-fixed">

                        {/* If you spend */}
                        <CardCalculationModal.Description>
                            <FormattedMessage
                                {...COMMON_MESSAGES.calculateHowMonthlySpending}
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value>
                            <FormattedMessage
                                {...COMMON_MESSAGES.calculateHowMonthlySpendingPerMonth}
                                values={{ monthlySpending: formatDollars(monthlySpendingAverage, intl, 0) }}
                                tagName="output"
                            />
                        </CardCalculationModal.Value>

                        {/* You’ll earn */}
                        <CardCalculationModal.Description>
                            <FormattedMessage
                                {...COMMON_MESSAGES.calculateHowYouWillEarn}
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value>
                            <FormattedMessage
                                id="edb.cc.calculations.you-will-earn"
                                defaultMessage="{firstYearRewardPoints, number, decimal0} pts/yr"
                                values={{ firstYearRewardPoints: firstYearRewardPoints }}
                                tagName="output"
                            />
                        </CardCalculationModal.Value>

                        {/* Which is worth */}
                        <CardCalculationModal.Description className="bringAttention">
                            <FormattedMessage
                                id="cc.results.calculate-how.which-is-worth"
                                defaultMessage="Which is worth"
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value>
                            <output className="bringAttention">
                                <FormattedMessage
                                    id="edb.cc.calculation.first-year-points"
                                    defaultMessage="{firstYearPointsInDollars, number, CAD0}/yr{newline}<small>({oneDollar, number, CAD0} = {pointsPerDollar, number, decimal0} points)</small>"
                                    values={{
                                        firstYearPointsInDollars: firstYearPointsInDollars,
                                        pointsPerDollar: firstYearRewardPoints / firstYearPointsInDollars,
                                        oneDollar: 1,
                                        ...DefaultIntlFormatValues,
                                    }}
                                />
                            </output>
                        </CardCalculationModal.Value>

                        {/* First year sign up bonus */}
                        <If condition={welcomeSignUp?.value}>
                            <CardCalculationModal.Description>
                                <FormattedMessage
                                    id="edb.cc.calculation.first-year-sign-up-bonus"
                                    defaultMessage="First year sign up bonus"
                                />
                                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                                <span className="operator">{'+'}</span>
                            </CardCalculationModal.Description>
                            <CardCalculationModal.Value>
                                <output>
                                    <FormattedNumber
                                        style="currency"
                                        currency="CAD"
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={0}
                                        value={welcomeSignUp.value}
                                    />
                                </output>
                            </CardCalculationModal.Value>
                        </If>

                        {/* First year spending bonus */}
                        <If condition={welcomeSpending?.value}>
                            <CardCalculationModal.Description>
                                <FormattedMessage
                                    id="edb.cc.calculation.first-year-spending-bonus"
                                    defaultMessage="First year spending bonus"
                                />
                                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                                <span className="operator">{'+'}</span>
                            </CardCalculationModal.Description>
                            <CardCalculationModal.Value>
                                <output>
                                    <FormattedNumber
                                        style="currency"
                                        currency="CAD"
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={0}
                                        value={welcomeSpending.value}
                                    />
                                </output>
                            </CardCalculationModal.Value>
                        </If>

                        {/* Gift offer */}
                        <If condition={giftCardOffer}>
                            <CardCalculationModal.Description>
                                <FormattedMessage
                                    id="edb.cc.calculation.gift offer"
                                    defaultMessage="Gift offer"
                                />
                                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                                <span className="operator">{'+'}</span>
                            </CardCalculationModal.Description>
                            <CardCalculationModal.Value>
                                <output>
                                    <FormattedNumber
                                        style="currency"
                                        currency="CAD"
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={0}
                                        value={giftCardOffer.maxDollarValue}
                                    />
                                </output>
                            </CardCalculationModal.Value>
                        </If>

                        {/* Annual fee */}
                        <AnnualFeeTermAndDefinition
                            annualFee={annualFee}
                            isFirstYear={true}
                            isFirstYearFeeWaived={isFirstYearFeeWaived}
                        />

                        {/* First year reward */}
                        <CardCalculationModal.Description
                            className="bringAttention isBottomSummary rh-mt-0_5 rh-py-1 rh-bg-yuzu-lightest"
                        >
                            <FormattedMessage
                                id="edb.cc.calculation.first-year-reward"
                                defaultMessage="First year reward"
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value className="isBottomSummary rh-bg-yuzu-lightest rh-mt-0_5 rh-py-1">
                            <output className={classNames('bringAttention', { 'fee': netFirstYearValue < 0 })}>
                                <FormattedNumber
                                    style="currency"
                                    currency="CAD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                    value={netFirstYearValue}
                                />
                            </output>
                        </CardCalculationModal.Value>
                    </CardCalculationModal.ContentGrid>
                </CardCalculationModal.DefinitionListWrapper>

                {/* Subsequent year calculations */}

                <CardCalculationModal.DefinitionListWrapper
                    className="space-above"
                    data-test-name="subsequent-year"
                >
                    <CardCalculationModal.ContentGrid className="second-column-fixed">
                        {/* Subsequent year points value */}
                        <CardCalculationModal.Description className="rh-mt-0_75">
                            <FormattedMessage
                                id="edb.cc.calculation.subsequent-year-points-value"
                                defaultMessage="Subsequent year points value"
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value className="rh-mt-0_75">
                            <output>
                                <FormattedNumber
                                    style="currency"
                                    currency="CAD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                    value={subsequentYearPointsInDollars}
                                />
                                <FormattedMessage
                                    {...COMMON_MESSAGES.perYear}
                                />
                            </output>
                        </CardCalculationModal.Value>


                        {/* Anniversary bonus */}
                        <If condition={anniversaryBonus.value}>
                            <CardCalculationModal.Description>
                                <FormattedMessage
                                    id="edb.cc.calculation.anniversary-bonus-value"
                                    defaultMessage="Anniversary bonus"
                                />
                                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                                <span className="operator">{'+'}</span>
                            </CardCalculationModal.Description>
                            <CardCalculationModal.Value>
                                <output>
                                    <FormattedNumber
                                        style="currency"
                                        currency="CAD"
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={0}
                                        value={anniversaryBonus.value}
                                    />
                                </output>
                            </CardCalculationModal.Value>
                        </If>

                        {/* Annual fee */}
                        <AnnualFeeTermAndDefinition
                            annualFee={annualFee}
                            isFirstYear={false}
                        />

                        {/* Subsequent year reward/yr (total) */}
                        <CardCalculationModal.Description
                            className="bringAttention isBottomSummary rh-mt-0_5 rh-py-1 rh-bg-yuzu-lightest"
                        >
                            <FormattedMessage
                                id="edb.cc.calculation.subsequent-year-reward"
                                defaultMessage="Subsequent year reward"
                            />
                        </CardCalculationModal.Description>
                        <CardCalculationModal.Value
                            className="isBottomSummary rh-bg-yuzu-lightest rh-mt-0_5 rh-py-1"
                        >
                            <output className={classNames('bringAttention', { 'fee': netFirstYearValue < 0 })}>
                                <FormattedNumber
                                    style="currency"
                                    currency="CAD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                    value={netSubsequentYearValue}
                                />
                                <FormattedMessage
                                    {...COMMON_MESSAGES.perYear}
                                />
                            </output>
                        </CardCalculationModal.Value>

                    </CardCalculationModal.ContentGrid>
                </CardCalculationModal.DefinitionListWrapper>
            </>
        </CardCalculationModal>
    );
}

CardCalculationModalRewards.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape).isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};


function AnnualFeeTermAndDefinition({
    annualFee,
    isFirstYear,
    isFirstYearFeeWaived = false,
}) {
    // must consider if this is displaying the first year or subsequent ones
    const isWaivingFirstYear = isFirstYear && isFirstYearFeeWaived;

    return (
        <>
            {/* Annual fee */}
            <CardCalculationModal.Description>
                <FormattedMessage
                    id="edb.cc.calculation.annual-fee"
                    defaultMessage="Annual fee"
                />
                {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                <span className="operator">{'-'}</span>
            </CardCalculationModal.Description>
            <CardCalculationModal.Value>
                <output className={classNames({ fee: !!annualFee && !isWaivingFirstYear })}>
                    <Choose>
                        <When condition={isWaivingFirstYear}>
                            <span className="rh-text-decoration-line-through rh-mr-0_5">
                                <FormattedNumber
                                    style="currency"
                                    currency="CAD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                    value={annualFee}
                                />
                            </span>
                            <FormattedNumber
                                style="currency"
                                currency="CAD"
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                                value={0}
                            />
                        </When>
                        <Otherwise>
                            <FormattedNumber
                                style="currency"
                                currency="CAD"
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                                value={annualFee}
                            />
                            <If condition={!isFirstYear}>
                                <FormattedMessage
                                    {...COMMON_MESSAGES.perYear}
                                />
                            </If>
                        </Otherwise>
                    </Choose>
                </output>
            </CardCalculationModal.Value>
        </>
    );
}

AnnualFeeTermAndDefinition.propTypes = {
    annualFee: PropTypes.number.isRequired,
    isFirstYear: PropTypes.bool.isRequired,
    isFirstYearFeeWaived: PropTypes.bool,
};


export default observer(CardCalculationModalRewards);
